import React from 'react';

const Context = ({ setPage, formData, setFormData }) => {
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      const updatedConcerns = checked 
        ? [...formData.concerns, value]
        : formData.concerns.filter(c => c !== value);
      setFormData({ ...formData, concerns: updatedConcerns });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const toggleCheckbox = (value) => {
    const checked = formData.concerns.includes(value);
    handleChange({ target: { name: 'concerns', value, type: 'checkbox', checked: !checked } });
  };

  return (
    <div className="animate-fade-in space-y-6">
      <div className="bg-white rounded-2xl p-8 shadow-sm">
        <h1 className="text-2xl md:text-3xl font-bold text-[#2E5FA7] mb-2">Business Priorities & Challenges</h1>
        <p className="text-gray-600 mb-8">Help us understand what matters most to your organization so we can align technology recommendations to your real business needs.</p>
        
        <div className="mb-8">
          <div className="text-[#2E5FA7] font-semibold text-base mb-4 flex items-center gap-2">📋 Company Information</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block font-semibold mb-2 text-sm text-gray-700">Company Name *</label>
              <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-[#2E5FA7] focus:ring-2 focus:ring-[#2E5FA7]/20 outline-none transition-all" placeholder="Enter company name" />
            </div>
            <div>
              <label className="block font-semibold mb-2 text-sm text-gray-700">Industry *</label>
              <select name="industry" value={formData.industry} onChange={handleChange} className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-[#2E5FA7] focus:ring-2 focus:ring-[#2E5FA7]/20 outline-none transition-all bg-white">
                <option value="">Select industry...</option>
                {['Healthcare', 'Legal', 'Financial Services', 'Manufacturing', 'Education', 'Government', 'Accounting', 'Real Estate', 'Insurance', 'Nonprofit', 'Technology', 'Construction', 'Professional Services', 'Other'].map(ind => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-2 text-sm text-gray-700">Number of Employees</label>
              <select name="employees" value={formData.employees} onChange={handleChange} className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-[#2E5FA7] focus:ring-2 focus:ring-[#2E5FA7]/20 outline-none transition-all bg-white">
                <option value="">Select range...</option>
                {['25-50', '51-100', '101-250', '251-500'].map(emp => (
                  <option key={emp} value={emp}>{emp}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-2 text-sm text-gray-700">Prepared By</label>
              <input type="text" name="preparedBy" value={formData.preparedBy} onChange={handleChange} className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-[#2E5FA7] focus:ring-2 focus:ring-[#2E5FA7]/20 outline-none transition-all" placeholder="Your name" />
            </div>
          </div>
        </div>
        
        <div className="mb-8">
          <div className="text-[#2E5FA7] font-semibold text-base mb-4 flex items-center gap-2">🎯 Business Priorities</div>
          <label className="block font-semibold mb-2 text-sm text-gray-700">What are your top 3 business priorities this year?</label>
          <textarea name="priorities" value={formData.priorities} onChange={handleChange} className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-[#2E5FA7] focus:ring-2 focus:ring-[#2E5FA7]/20 outline-none transition-all min-h-[100px] resize-y" placeholder="Example: Expand to new markets, improve operational efficiency, reduce costs..."></textarea>
        </div>
        
        <div className="mb-8">
          <div className="text-[#2E5FA7] font-semibold text-base mb-4 flex items-center gap-2">⚠️ Technology Friction Points</div>
          <label className="block font-semibold mb-2 text-sm text-gray-700">Where does technology create friction today?</label>
          <textarea name="friction" value={formData.friction} onChange={handleChange} className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-[#2E5FA7] focus:ring-2 focus:ring-[#2E5FA7]/20 outline-none transition-all min-h-[100px] resize-y" placeholder="Example: Slow systems, frequent downtime, security concerns, difficult to scale..."></textarea>
        </div>
        
        <div className="mb-8">
          <div className="text-[#2E5FA7] font-semibold text-base mb-4 flex items-center gap-2">🔥 Primary Concerns</div>
          <label className="block font-semibold mb-2 text-sm text-gray-700">What concerns you most? (Select all that apply)</label>
          <div className="flex flex-wrap gap-2.5">
            {[
              { val: 'cost', label: '💰 Cost & Budget' },
              { val: 'security', label: '🔒 Security & Compliance' },
              { val: 'downtime', label: '⏱️ Downtime & Reliability' },
              { val: 'growth', label: '📈 Growth Limitations' },
              { val: 'compliance', label: '📋 Regulatory Compliance' },
              { val: 'productivity', label: '⚡ Productivity & Efficiency' }
            ].map(concern => (
              <div 
                key={concern.val}
                onClick={() => toggleCheckbox(concern.val)}
                className={`
                  flex items-center gap-2 px-4 py-2.5 rounded-lg cursor-pointer transition-all border-2
                  ${formData.concerns.includes(concern.val) 
                    ? 'bg-[#2E5FA7]/10 border-[#2E5FA7]' 
                    : 'bg-[#F4F6F9] border-transparent hover:bg-gray-200'}
                `}
              >
                <span className="text-sm font-medium">{concern.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="mb-8">
          <div className="text-[#2E5FA7] font-semibold text-base mb-4 flex items-center gap-2">💥 Recent Incidents</div>
          <label className="block font-semibold mb-2 text-sm text-gray-700">Any recent incidents or frustrations worth mentioning?</label>
          <textarea name="incidents" value={formData.incidents} onChange={handleChange} className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-[#2E5FA7] focus:ring-2 focus:ring-[#2E5FA7]/20 outline-none transition-all min-h-[100px] resize-y" placeholder="Example: Recent security scare, major printer breakdown, system outage last month..."></textarea>
        </div>
        
        <div className="flex justify-between mt-8">
          <button onClick={() => setPage('welcome')} className="px-6 py-3 rounded-lg font-semibold text-[#2E5FA7] border-2 border-[#2E5FA7] hover:bg-gray-50 transition-all">← Back</button>
          <button onClick={() => setPage('alignment')} className="px-6 py-3 rounded-lg font-semibold bg-[#2E5FA7] text-white hover:bg-[#244C86] transition-all shadow-md">Continue to Alignment Review →</button>
        </div>
      </div>
    </div>
  );
};

export default Context;